import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ortellyLogo } from "@/assets/images";

const LOGO_URL = ortellyLogo;

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/products", label: "Products" },
  { to: "/services", label: "Services" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const linkClass = ({ isActive }) =>
    `relative text-sm font-medium transition-colors ${
      isActive ? "text-[#0046B5]" : "text-slate-600 hover:text-[#0046B5]"
    }`;

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-slate-200 bg-white/85 backdrop-blur-md"
    >
      <div className="container-section flex h-16 items-center justify-between md:h-20">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="flex items-center gap-2"
          aria-label="Ortelly Systems — Home"
        >
          <img
            src={LOGO_URL}
            alt="Ortelly Systems Private Limited"
            className="h-10 w-auto object-contain md:h-12"
          />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={linkClass}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <Button
            variant="outline"
            data-testid="header-contact-btn"
            onClick={() => navigate("/contact")}
            className="rounded-md border-slate-200 text-slate-900 hover:text-[#0046B5]"
          >
            Contact
          </Button>
          <Button
            data-testid="header-demo-btn"
            onClick={() => navigate("/products#demo")}
            className="rounded-md bg-[#0046B5] text-white shadow-sm hover:bg-[#003A99]"
          >
            Get a Demo
          </Button>
        </div>

        {/* Mobile */}
        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="mobile-menu-btn"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] p-0">
              <SheetTitle className="sr-only">Main Navigation</SheetTitle>
              <SheetDescription className="sr-only">Primary navigation menu for Ortelly Systems website</SheetDescription>
              <div className="flex items-center justify-between border-b border-slate-200 p-4">
                <img src={LOGO_URL} alt="Ortelly" className="h-9 w-auto object-contain" />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-menu-close"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <nav className="flex flex-col p-4" aria-label="Mobile">
                {NAV.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `rounded-md px-3 py-3 text-base font-medium ${
                        isActive
                          ? "bg-slate-50 text-[#0046B5]"
                          : "text-slate-700 hover:bg-slate-50 hover:text-[#0046B5]"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
                <div className="mt-4 flex flex-col gap-2 border-t border-slate-200 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setOpen(false);
                      navigate("/contact");
                    }}
                    data-testid="mobile-contact-btn"
                    className="w-full"
                  >
                    Contact
                  </Button>
                  <Button
                    data-testid="mobile-demo-btn"
                    onClick={() => {
                      setOpen(false);
                      navigate("/products#demo");
                    }}
                    className="w-full bg-[#0046B5] text-white hover:bg-[#003A99]"
                  >
                    Get a Demo
                  </Button>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
